// components/Dashboard/Sidebar.tsx
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    ClipboardList, Timer, CheckCircle, Settings,
    MessageSquare, Star, DollarSign, CreditCard, Menu, X, Calendar
} from 'lucide-react';
import { authService } from '../../services/authService';
import { useLanguage } from '../../hooks/useLanguage';
import { appointmentService } from '../../services/appointmentService';

interface SidebarProps {
    creditBalance?: { standard: number; ai: number } | null;
}

export const Sidebar: React.FC<SidebarProps> = ({ creditBalance }) => {
    const location = useLocation();
    const { t, isRTL } = useLanguage();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [pendingAppointments, setPendingAppointments] = useState(0);

    // Badge « Agenda » : demandes de rendez-vous en attente de réponse du garage
    useEffect(() => {
        let cancelled = false;
        const load = () => appointmentService.getStats()
            .then(r => { if (!cancelled) setPendingAppointments(r.data?.awaitingGarage || 0); })
            .catch(() => undefined);
        load();
        const timer = setInterval(load, 2 * 60 * 1000);
        return () => { cancelled = true; clearInterval(timer); };
    }, []);

    const menuItems = [
        { title: t('sidebar.menu.pending'),    icon: <ClipboardList size={20} />, path: '/dashboard/pending' },
        { title: t('sidebar.menu.inProgress'), icon: <Timer size={20} />,         path: '/dashboard/in-progress' },
        { title: t('sidebar.menu.completed'),  icon: <CheckCircle size={20} />,   path: '/dashboard/completed' },
        { title: t('sidebar.menu.messages'),   icon: <MessageSquare size={20} />, path: '/dashboard/messages' },
        { title: t('sidebar.menu.reviews'),    icon: <Star size={20} />,          path: '/dashboard/reviews' },
        { title: t('sidebar.menu.appointments'), icon: <Calendar size={20} />,      path: '/dashboard/appointments', badge: pendingAppointments },
        { title: t('sidebar.menu.finances'),   icon: <DollarSign size={20} />,    path: '/dashboard/finances' },
        { title: t('sidebar.menu.settings'),   icon: <Settings size={20} />,      path: '/dashboard/settings' },
    ];

    const SidebarInner = () => (
        <div className="flex flex-col h-full w-64 bg-gray-800 text-white" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="p-4 flex items-center justify-between flex-shrink-0">
                <div>
                    <h2 className="text-xl font-bold">
                        <span className="text-indigo-400">{t('sidebar.title')}</span>
                    </h2>
                    <p className="text-sm text-gray-400 mt-1">{t('sidebar.subtitle')}</p>
                </div>
                <button className="lg:hidden p-1 rounded hover:bg-gray-700" onClick={() => setMobileOpen(false)}>
                    <X size={20} />
                </button>
            </div>

            {creditBalance && (
                <div className="mx-4 mb-4 p-3 bg-gray-700 rounded-lg flex-shrink-0">
                    <div className="flex items-center mb-2 gap-2">
                        <CreditCard size={16} className="text-indigo-400" />
                        <span className="text-sm font-medium">{t('sidebar.credits.title')}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="bg-gray-600 p-2 rounded text-center">
                            <span className="text-xs text-gray-300">{t('sidebar.credits.standard')}</span>
                            <p className="text-lg font-bold text-white">{creditBalance.standard}</p>
                        </div>
                        <div className="bg-gray-600 p-2 rounded text-center">
                            <span className="text-xs text-gray-300">{t('sidebar.credits.ai')}</span>
                            <p className="text-lg font-bold text-white">{creditBalance.ai}</p>
                        </div>
                    </div>
                </div>
            )}

            <nav className="flex-1 mt-2 overflow-y-auto">
                {menuItems.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                            location.pathname === item.path ? 'bg-indigo-600' : 'hover:bg-gray-700'
                        }`}
                    >
                        {item.icon}
                        <span className="flex-1">{item.title}</span>
                        {'badge' in item && (item as any).badge > 0 && (
                            <span className="min-w-[1.25rem] px-1.5 text-center rounded-full bg-amber-500 text-white text-xs font-bold">{(item as any).badge}</span>
                        )}
                    </Link>
                ))}
            </nav>

            <div className="p-4 border-t border-gray-700 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">
                            {authService.getCurrentUser()?.email.charAt(0).toUpperCase()}
                        </span>
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{authService.getCurrentUser()?.email}</p>
                        <p className="text-xs text-gray-400">{t('sidebar.profile.role')}</p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {/* Burger — mobile only */}
            <button
                className="lg:hidden fixed top-3 left-3 z-50 p-2 bg-gray-800 text-white rounded-lg shadow-lg"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
            >
                <Menu size={22} />
            </button>

            {/* Overlay — mobile only */}
            {mobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Mobile sidebar: fixed, out of flow */}
            <div
                className={`
                    lg:hidden fixed top-0 left-0 h-full z-50
                    transform transition-transform duration-300 ease-in-out
                    ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
                `}
            >
                <SidebarInner />
            </div>

            {/* Desktop sidebar: in normal flow, always visible */}
            <div className="hidden lg:flex h-full flex-shrink-0">
                <SidebarInner />
            </div>
        </>
    );
};